import React, { useState, useEffect } from 'react'
import SidePanel from '@/components/ui/SidePanel'
import Button from '@/components/ui/Button'
import Tag from '@/components/ui/Tag'
import ReportTemplateSelect from '@/components/ui/ReportTemplateSelect'
import ReportWritePanel from './ReportWritePanel'
import { Download, Edit, Edit2, Check } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'
import { TaskReportService } from '@/api/generated/services/TaskReportService'
import type { TaskWithReportResponse } from '@/api/generated/models/TaskWithReportResponse'
import type { TaskReportDetailResponse } from '@/api/generated/models/TaskReportDetailResponse'
import type { ReportTemplateDetailResponse } from '@/api/generated/models/ReportTemplateDetailResponse'
import type { ReportUpdateRequest } from '@/api/generated/models/ReportUpdateRequest'
import { useAlert } from '@/components/ui/Alert/exports'
import { handleApiError, ErrorMessages } from '@/utils/api-error-handler'

/**
 * GetTaskReportDetail 응답(task 필드들이 root에 spread)을 기존 JSX가 쓰는
 * TaskWithReportResponse({ task, report, report_template }) 형태로 변환.
 * 이로써 본문 JSX의 selectedReport.task.X 패턴을 그대로 유지.
 */
function adaptDetailToWithReport(d: TaskReportDetailResponse): TaskWithReportResponse {
  return {
    task: {
      id: d.id,
      task_subgroup_id: d.task_subgroup_id,
      task_subgroup_name: d.task_subgroup_name,
      task_group_id: d.task_group_id,
      task_group_name: d.task_group_name,
      name: d.name,
      workspace_id: d.origin_workspace_id,
      workspace_name: d.origin_workspace_name,
      business_id: d.business_id,
      business_name: d.business_name,
      department_id: d.department_id,
      department_name: d.department_name,
      assignee: d.assignee,
      sub_assignee: d.sub_assignee,
      manager: d.manager,
      reviewer: d.reviewer,
      start_at: d.start_at,
      end_at: d.end_at,
      status: d.status,
      // TaskReportDetailResponse엔 performance_status가 없어 빈 값으로 채움
      performance_status: '',
    },
    report: d.report,
    report_template: d.report_template,
  }
}

// 한글(HWPX) 다운로드 버튼은 숨긴다 (2026-10-30 요청). 다시 보이려면 true 로 바꾼다.
const SHOW_HWPX_DOWNLOAD = false

interface ReportDetailPanelProps {
  isOpen: boolean
  onClose: () => void
  /** 표시할 세부실행과제 ID */
  taskId: string | null
  /** 표시할 보고서 템플릿 key */
  reportTemplateKey: string | null
  /** 부모가 저장/외부 변경 후 이 값을 증가시키면 패널이 자체적으로 재조회 */
  refreshKey?: number
  reportTemplates: ReportTemplateDetailResponse[]
  isReportTemplatesLoading: boolean
  reportTemplatesError: string | null
  workspaceId: string
  /** 보고서 유형(템플릿) 변경 시 부모에 알림 — 부모가 reportTemplateKey prop을 갱신해 줘야 패널이 재fetch */
  onReportTypeChange: (reportKey: string, reportName: string) => void
  onEditTask?: (taskId: string) => void
  onReportUpdate?: () => void
  onTaskClick?: (taskId: string) => void
}

// Reusable Section Header Component
const SectionHeader: React.FC<{
  title: string
  rightContent?: React.ReactNode
}> = ({ title, rightContent }) => (
  <div
    className="pb-2 border-b flex items-center justify-between"
    style={{
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '18px',
        fontWeight: 700,
        color: designTokens.colors.gray[6]
      }}
    >
      {title}
    </h3>
    {rightContent}
  </div>
)

// Reusable Info Field Component
const InfoField: React.FC<{
  label: string
  value: string | React.ReactNode
  fullWidth?: boolean
}> = ({ label, value, fullWidth = false }) => (
  <div className={`flex ${fullWidth ? 'flex-col gap-2' : 'items-center gap-5'}`}>
    <div className={fullWidth ? '' : 'w-37 flex-shrink-0'}>
      <span
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}
      >
        {label}
      </span>
    </div>
    <div className={fullWidth ? '' : 'flex-1 py-2'}>
      {typeof value === 'string' ? (
        <span
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '16px',
            fontWeight: 400,
            color: designTokens.colors.gray[6]
          }}
        >
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  </div>
)

const ReportDetailPanel: React.FC<ReportDetailPanelProps> = ({
  isOpen,
  onClose,
  taskId,
  reportTemplateKey,
  refreshKey,
  reportTemplates,
  isReportTemplatesLoading,
  reportTemplatesError,
  workspaceId,
  onReportTypeChange,
  onEditTask,
  onReportUpdate,
  onTaskClick,
}) => {
  const { showError } = useAlert()

  // ===== 자체 데이터 로드 =====
  const [selectedReport, setSelectedReport] = useState<TaskWithReportResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [isDownloading, setIsDownloading] = useState(false)
  const [isDownloadingHwpx, setIsDownloadingHwpx] = useState(false)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [previewHtml, setPreviewHtml] = useState<string>('')
  const [previewError, setPreviewError] = useState<string>('')

  // Report write panel state
  const [isReportWritePanelOpen, setIsReportWritePanelOpen] = useState(false)

  // Loading state to prevent race condition during data refresh
  const [isRefreshingReport, setIsRefreshingReport] = useState(false)

  // Grade update state
  const [selectedGrade, setSelectedGrade] = useState<string>('')
  const [isUpdatingGrade, setIsUpdatingGrade] = useState(false)

  // taskId/reportTemplateKey/refreshKey 변경 시 자체 fetch
  useEffect(() => {
    if (!isOpen || !taskId || !reportTemplateKey) {
      setSelectedReport(null)
      setLoadError(null)
      return
    }
    let cancelled = false
    setIsLoading(true)
    setLoadError(null)
    TaskReportService.appsTaskViewsGetTaskReportDetail({
      taskId,
      reportTemplateId: reportTemplateKey,
    })
      .then(res => {
        if (cancelled) return
        const adapted = adaptDetailToWithReport(res)
        setSelectedReport(adapted)
        setSelectedGrade(adapted.report?.grade || '')
      })
      .catch(err => {
        if (cancelled) return
        console.error('Failed to load report detail:', err)
        handleApiError(err, showError, ErrorMessages.FETCH_FAILED)
        setLoadError('보고서를 불러오지 못했습니다.')
        setSelectedReport(null)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, taskId, reportTemplateKey, refreshKey, showError])

  const handleDownloadPdf = async () => {
    if (!selectedReport?.report_template?.key) {
      alert('보고서 템플릿 정보가 없습니다.')
      return
    }

    try {
      setIsDownloading(true)

      /**
       * TaskReportService.appsTaskViewsGenerateTaskReportPdf 대신 직접 axios를 사용하는 이유:
       *
       * 1. OpenAPI 클라이언트 한계:
       *    - OpenAPI 생성 클라이언트는 기본적으로 JSON 응답을 가정하여 설계됨
       *    - PDF와 같은 바이너리 데이터를 responseType 지정 없이 처리하면 문자열로 변환되어 손상됨
       *
       * 2. 바이너리 데이터 처리 문제:
       *    - TaskReportService 사용 시 PDF 바이너리가 String으로 반환되어 파일 손상 발생
       *    - 문자열로 변환된 바이너리를 다시 올바른 형태로 복원하는 것은 복잡하고 불안정함
       *
       * 3. 해결책:
       *    - axios를 직접 사용하여 responseType: 'blob' 명시적 설정
       *    - 바이너리 데이터를 손실 없이 Blob 형태로 직접 수신
       *    - 프로젝트의 인증 시스템(getStoredTokens)과 환경 설정(VITE_API_BASE_URL) 활용
       */

      const axios = (await import('axios')).default
      const { accessToken } = (await import('@/store/authSlice')).getStoredTokens()
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || ''

      const response = await axios({
        method: 'GET',
        url: `${apiBaseUrl}/api/task/${selectedReport.task.id}/report/${selectedReport.report_template.key}/pdf`,
        params: { download: true },
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Accept': 'application/pdf'
        },
        responseType: 'blob' // 바이너리 데이터 손실 방지를 위한 핵심 설정
      })

      const blob = response.data

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl

      // Create filename with task name and template name
      const taskName = selectedReport.task.name.replace(/[^a-zA-Z0-9가-힣\s]/g, '')
      const templateName = selectedReport.report_template.name.replace(/[^a-zA-Z0-9가-힣\s]/g, '')
      link.download = `${taskName}_${templateName}_보고서.pdf`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)

    } catch (error) {
      console.error('PDF 다운로드 실패:', error)
      alert('PDF 다운로드에 실패했습니다.')
    } finally {
      setIsDownloading(false)
    }
  }

  const handleDownloadHwpx = async () => {
    if (!selectedReport?.report_template?.key) {
      alert('보고서 템플릿 정보가 없습니다.')
      return
    }

    try {
      setIsDownloadingHwpx(true)

      /**
       * TaskReportService.appsTaskViewsGenerateTaskReportHwpx 대신 직접 axios를 사용하는 이유:
       * - PDF 다운로드(handleDownloadPdf)와 동일하게 OpenAPI 클라이언트는 바이너리(HWPX)
       *   데이터를 responseType 지정 없이 처리하면 문자열로 변환되어 파일이 손상됨
       * - axios를 직접 사용하여 responseType: 'blob'을 명시적으로 설정해 손실 없이 수신
       */

      const axios = (await import('axios')).default
      const { accessToken } = (await import('@/store/authSlice')).getStoredTokens()
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || ''

      const response = await axios({
        method: 'GET',
        url: `${apiBaseUrl}/api/task/${selectedReport.task.id}/report/${selectedReport.report_template.key}/hwpx`,
        params: { download: true },
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Accept': 'application/octet-stream'
        },
        responseType: 'blob' // 바이너리 데이터 손실 방지를 위한 핵심 설정
      })

      const blob = response.data

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl

      // Create filename with task name and template name
      const taskName = selectedReport.task.name.replace(/[^a-zA-Z0-9가-힣\s]/g, '')
      const templateName = selectedReport.report_template.name.replace(/[^a-zA-Z0-9가-힣\s]/g, '')
      link.download = `${taskName}_${templateName}_보고서.hwpx`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)

    } catch (error) {
      console.error('한글 다운로드 실패:', error)
      alert('한글 다운로드에 실패했습니다.')
    } finally {
      setIsDownloadingHwpx(false)
    }
  }

  const loadReportPreview = async () => {
    if (!selectedReport?.report_template?.key) {
      setPreviewError('보고서 템플릿 정보가 없습니다.')
      return
    }

    try {
      setIsLoadingPreview(true)
      setPreviewError('')

      /**
       * HTML 미리보기를 위해 직접 axios를 사용하는 이유:
       * - OpenAPI 클라이언트는 HTML 응답에 대해 responseType 설정이 어려움
       * - 텍스트 응답을 올바르게 받기 위해 responseType: 'text' 명시적 설정 필요
       */

      const axios = (await import('axios')).default
      const { accessToken } = (await import('@/store/authSlice')).getStoredTokens()
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || ''

      const response = await axios({
        method: 'GET',
        url: `${apiBaseUrl}/api/task/${selectedReport.task.id}/report/${selectedReport.report_template.key}/html`,
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Accept': 'text/html'
        },
        responseType: 'text' // HTML 텍스트 응답을 위한 설정
      })

      setPreviewHtml(response.data)

    } catch (error) {
      console.error('보고서 미리보기 로딩 실패:', error)
      setPreviewError('보고서 미리보기를 불러오는데 실패했습니다.')
    } finally {
      setIsLoadingPreview(false)
    }
  }

  // Clear preview when panel closes
  React.useEffect(() => {
    if (!isOpen) {
      // Clear all preview states when panel is closed
      setPreviewHtml('')
      setPreviewError('')
      setIsLoadingPreview(false)
      setIsReportWritePanelOpen(false)
    }
  }, [isOpen])

  // Auto-select first template if none is selected.
  // reportTemplateKey가 비어있는데 taskId만 있을 때 자동으로 첫 템플릿 알려서 부모가 prop 갱신.
  React.useEffect(() => {
    if (
      isOpen &&
      taskId &&
      !reportTemplateKey &&
      reportTemplates.length > 0 &&
      !isReportTemplatesLoading
    ) {
      const firstTemplate = reportTemplates[0]
      onReportTypeChange(firstTemplate.key, firstTemplate.name)
    }
  }, [isOpen, taskId, reportTemplateKey, reportTemplates, isReportTemplatesLoading, onReportTypeChange])

  // Auto-reload preview when report template changes
  React.useEffect(() => {
    if (selectedReport?.report_template?.key && isOpen) {
      // Clear previous preview and reload
      setPreviewHtml('')
      setPreviewError('')
      loadReportPreview()
    }
  }, [selectedReport?.report_template?.key, isOpen])

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'completed':
        return { label: '완료', color: 'success' as const }
      case 'incomplete':
        return { label: '미완료', color: 'error' as const }
      default:
        return { label: status || '-', color: 'default' as const }
    }
  }

  // Grade options
  const gradeOptions = [
    { value: '', label: '평가 없음' },
    { value: 'S', label: 'S' },
    { value: 'A', label: 'A' },
    { value: 'B', label: 'B' },
    { value: 'C', label: 'C' },
    { value: 'D', label: 'D' }
  ]

  // Handle grade update
  const handleGradeUpdate = async () => {
    if (!selectedReport?.report?.id) {
      alert('보고서 정보가 없습니다.')
      return
    }

    try {
      setIsUpdatingGrade(true)

      const updateRequest: ReportUpdateRequest = {
        grade: selectedGrade || null
      }

      await TaskReportService.appsTaskViewsUpdateTaskReportEndpoint({
        reportId: selectedReport.report.id,
        requestBody: updateRequest
      })

      // Update local state to reflect the change (optimistic — refreshKey 없이도 즉시 반영)
      if (selectedReport) {
        const updatedReport = {
          ...selectedReport,
          report: selectedReport.report ? {
            ...selectedReport.report,
            grade: selectedGrade || null
          } : null
        }
        setSelectedReport(updatedReport as TaskWithReportResponse)
      }

      // Notify parent to refresh report list
      onReportUpdate?.()

    } catch (error) {
      console.error('평가 등급 업데이트 실패:', error)
      alert('평가 등급 업데이트에 실패했습니다.')
    } finally {
      setIsUpdatingGrade(false)
    }
  }

  // Get grade tag color
  const getGradeTagColor = (grade: string | null | undefined) => {
    switch (grade) {
      case 'S':
        return 'secondary' as const
      case 'A':
        return 'success' as const
      case 'B':
        return 'info' as const
      case 'C':
        return 'warning' as const
      case 'D':
        return 'error' as const
      default:
        return 'default' as const
    }
  }


  const formatDateOnly = (dateString: string | null) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    return `${year}. ${month}. ${day}.`
  }

  const getHierarchyDisplay = () => {
    if (!selectedReport) return '-'

    const parts = []
    if (selectedReport.task.business_name) parts.push(selectedReport.task.business_name)
    if (selectedReport.task.task_group_name) parts.push(selectedReport.task.task_group_name)
    if (selectedReport.task.task_subgroup_name) parts.push(selectedReport.task.task_subgroup_name)

    return parts.join(' > ') || '-'
  }

  // 패널 닫힘
  if (!isOpen) return null

  // 로딩/에러/빈 상태 → 패널은 열린 채로 placeholder
  if (!selectedReport) {
    return (
      <SidePanel
        isOpen={isOpen}
        onClose={onClose}
        title="보고서 상세"
        width={800}
      >
        <div className="flex items-center justify-center" style={{ minHeight: '400px' }}>
          {loadError ? (
            <span style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              color: designTokens.colors.error.DEFAULT,
            }}>
              {loadError}
            </span>
          ) : isLoading ? (
            <div className="flex flex-col items-center gap-3">
              <div
                className="animate-spin rounded-full h-8 w-8 border-b-2"
                style={{ borderColor: designTokens.colors.primary.DEFAULT }}
              />
              <span style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '14px',
                color: designTokens.colors.gray[5],
              }}>
                데이터를 불러오는 중...
              </span>
            </div>
          ) : (
            <span style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              color: designTokens.colors.gray[4],
            }}>
              표시할 항목이 없습니다.
            </span>
          )}
        </div>
      </SidePanel>
    )
  }

  // Handle report write (create/edit)
  const handleEdit = () => {
    setIsReportWritePanelOpen(true)
  }

  // Handle report write success
  const handleReportWriteSuccess = async () => {
    // Set loading state to prevent race condition
    setIsRefreshingReport(true)

    try {
      // 1. Notify parent to refresh report list
      onReportUpdate?.()

      // 2. 단건 GetTaskReportDetail로 최신 보고서 데이터 가져와 내부 상태 갱신
      //    (이전 구현은 전체 워크스페이스 보고서를 fetch한 뒤 클라이언트에서 검색했으나, 단건 GET이 가벼움)
      if (taskId && reportTemplateKey) {
        try {
          const fresh = await TaskReportService.appsTaskViewsGetTaskReportDetail({
            taskId,
            reportTemplateId: reportTemplateKey,
          })
          setSelectedReport(adaptDetailToWithReport(fresh))
        } catch (error) {
          console.error('Failed to refresh report details after update:', error)
        }
      }

      // 3. Reload preview after successful save
      loadReportPreview()

      // 4. Close write panel
      setIsReportWritePanelOpen(false)
    } finally {
      // Always clear loading state
      setIsRefreshingReport(false)
    }
  }

  // Header actions with report type selector and edit button
  const headerActions = (
    <div className="flex items-center gap-3">
      {/* Report Type Selector */}
      <div className="w-52">
        <ReportTemplateSelect
          workspaceId={workspaceId}
          templates={reportTemplates}
          isLoading={isReportTemplatesLoading}
          templatesError={reportTemplatesError}
          selectedReportTemplateId={selectedReport?.report_template?.key || ''}
          selectedReportTemplateName={selectedReport?.report_template?.name || ''}
          onSelect={onReportTypeChange}
          placeholder="보고서 유형 선택"
          showAllOption={false}
        />
      </div>

      {/* 수정 패널을 연결하지 않은 화면(onEditTask 미전달)에서는 눌러도 반응이 없으므로 숨긴다 */}
      {onEditTask && (
        <Button
          size="small"
          color="dark"
          className="flex items-center gap-1"
          onClick={() => onEditTask(selectedReport?.task.id || '')}
          disabled={!selectedReport?.task.id}
        >
          <Edit2 className="w-4 h-4" />
          세부실행과제 수정
        </Button>
      )}
    </div>
  )

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title="보고서 상세"
      width={800}
      headerActions={headerActions}
    >
      <div className="p-6 space-y-6">
        {selectedReport && (
          <>
            {/* Section 2: 보고서 미리보기 */}
            <InfoField
              label="세부실행과제명"
              value={
                onTaskClick ? (
                  <button
                    onClick={() => onTaskClick(selectedReport.task.id)}
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      fontWeight: 400,
                      color: designTokens.colors.primary.DEFAULT,
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      textAlign: 'left'
                    }}
                  >
                    {selectedReport.task.name}
                  </button>
                ) : (
                  selectedReport.task.name
                )
              }
            />

            {/* 평가 등급 (Grade) Section */}
            <InfoField
              label="평가 등급"
              value={
                selectedReport.report ? (
                  <div className="flex items-center gap-3">
                    <select
                      value={selectedGrade}
                      onChange={(e) => setSelectedGrade(e.target.value)}
                      disabled={isUpdatingGrade}
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '14px',
                        fontWeight: 400,
                        color: designTokens.colors.gray[6],
                        padding: '6px 12px',
                        paddingRight: '30px',
                        border: `1px solid ${designTokens.colors.gray[2]}`,
                        borderRadius: '6px',
                        backgroundColor: '#fff',
                        cursor: isUpdatingGrade ? 'not-allowed' : 'pointer',
                        outline: 'none',
                        appearance: 'none',
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238E8E8E' d='M2.5 4.5L6 8l3.5-3.5'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 10px center',
                        minWidth: '120px'
                      }}
                    >
                      {gradeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {selectedGrade !== (selectedReport.report?.grade || '') && (
                      <Button
                        size="small"
                        color="dark"
                        onClick={handleGradeUpdate}
                        disabled={isUpdatingGrade}
                        style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                      >
                        <Check size={14} />
                        {isUpdatingGrade ? '저장 중...' : '저장'}
                      </Button>
                    )}
                    {selectedGrade !== (selectedReport.report?.grade || '') && selectedReport.report?.grade && (
                      <Tag color={getGradeTagColor(selectedReport.report.grade)}>
                        기존: {selectedReport.report.grade}
                      </Tag>
                    )}
                  </div>
                ) : (
                  <span style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '14px',
                    color: designTokens.colors.gray[4]
                  }}>
                    보고서가 없습니다. 보고서 작성을 먼저 진행해주세요.
                  </span>
                )
              }
            />

            <div className="space-y-2">
              <SectionHeader
                title="보고서 미리보기"
                rightContent={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Button
                      size="small"
                      color="light"
                      onClick={handleEdit}
                      disabled={isRefreshingReport}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <Edit size={16} />
                      {isRefreshingReport ? '데이터 갱신 중...' : '보고서 작성'}
                    </Button>
                    {SHOW_HWPX_DOWNLOAD && (
                      <Button
                        size="small"
                        color="dark"
                        onClick={handleDownloadHwpx}
                        disabled={isDownloadingHwpx || !selectedReport.report_template?.key}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        <Download size={16} />
                        {isDownloadingHwpx ? '한글 생성 중...' : '한글 다운로드'}
                      </Button>
                    )}
                    <Button
                      size="small"
                      color="dark"
                      onClick={handleDownloadPdf}
                      disabled={isDownloading || !selectedReport.report_template?.key}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <Download size={16} />
                      {isDownloading ? 'PDF 생성 중...' : 'PDF 다운로드'}
                    </Button>
                    <Button
                      size="small"
                      color="light"
                      onClick={loadReportPreview}
                      disabled={isLoadingPreview || !selectedReport.report_template?.key}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      {isLoadingPreview ? '로딩 중...' : '미리보기 새로고침'}
                    </Button>
                  </div>
                }
              />
              <div className="space-y-2" style={{ padding: '0px 4px' }}>
                {isLoadingPreview ? (
                  <div
                    className="flex items-center justify-center p-8"
                    style={{
                      backgroundColor: designTokens.colors.gray[0],
                      borderRadius: '8px',
                      minHeight: '200px'
                    }}
                  >
                    <div style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '14px',
                      color: designTokens.colors.gray[5]
                    }}>
                      보고서를 불러오는 중입니다...
                    </div>
                  </div>
                ) : previewError ? (
                  <div
                    className="flex items-center justify-center p-8"
                    style={{
                      backgroundColor: designTokens.colors.error.light,
                      borderRadius: '8px',
                      minHeight: '200px'
                    }}
                  >
                    <div style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '14px',
                      color: designTokens.colors.error.dark
                    }}>
                      {previewError}
                    </div>
                  </div>
                ) : previewHtml ? (
                  <iframe
                    srcDoc={previewHtml}
                    sandbox="allow-same-origin"
                    style={{
                      width: '100%',
                      height: '650px',
                      border: `1px solid ${designTokens.colors.gray[2]}`,
                      borderRadius: '5px',
                      backgroundColor: '#ffffff'
                    }}
                    title="보고서 미리보기"
                  />
                ) : !selectedReport.report_template?.key ? (
                  <div
                    className="flex flex-col items-center justify-center p-8"
                    style={{
                      backgroundColor: designTokens.colors.warning.light,
                      borderRadius: '8px',
                      minHeight: '200px'
                    }}
                  >
                    <div style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '14px',
                      color: designTokens.colors.warning.dark,
                      textAlign: 'center',
                      marginBottom: '8px'
                    }}>
                      보고서 미리보기를 보려면 먼저 보고서 유형을 선택해주세요.
                    </div>
                    <div style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '12px',
                      color: designTokens.colors.gray[5],
                      textAlign: 'center'
                    }}>
                      위의 '보고서 유형' 드롭다운에서 원하는 유형을 선택하세요.
                    </div>
                  </div>
                ) : (
                  <div
                    className="flex flex-col items-center justify-center p-8"
                    style={{
                      backgroundColor: designTokens.colors.gray[0],
                      borderRadius: '8px',
                      minHeight: '200px'
                    }}
                  >
                    <div style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '14px',
                      color: designTokens.colors.gray[5],
                      textAlign: 'center',
                      marginBottom: '12px'
                    }}>
                      보고서 미리보기를 불러오려면 버튼을 클릭하세요.
                    </div>
                    <Button
                      size="small"
                      color="dark"
                      onClick={loadReportPreview}
                      disabled={!selectedReport.report_template?.key}
                    >
                      미리보기 보기
                    </Button>
                  </div>
                )}
              </div>
            </div>




          </>
        )}
      </div>

      {/* Report Write Panel */}
      <ReportWritePanel
        isOpen={isReportWritePanelOpen}
        onClose={() => setIsReportWritePanelOpen(false)}
        selectedReport={selectedReport}
        onSuccess={handleReportWriteSuccess}
      />
    </SidePanel>
  )
}

export default ReportDetailPanel